import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import {
  FileSpreadsheet,
  FileText,
  FileType,
  Download,
  Loader2,
  GripVertical,
  Trash2,
  Plus,
  ArrowLeft,
  Play,
  Filter,
  X,
  Save,
  Database,
  Columns,
  Rows,
  Package,
  Users,
  Ticket,
  Shield,
  Building2,
  Calendar,
  User,
  Mail,
  Phone,
  Briefcase,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Hash,
  Type,
  ToggleLeft,
  RefreshCw,
  Eye,
  Search,
  UserPlus,
  CalendarDays,
  CalendarCog,
  FileStack
} from "lucide-react";

// Icon mapping for dynamic icons
const ICON_MAP = {
  Package, Users, Ticket, Shield, Building2, Calendar, User, Briefcase,
  FileText, UserPlus, CalendarDays, CalendarCog, FileStack
};

export default function ReportesPersonalizadosPage() {
  const [fuentesDatos, setFuentesDatos] = useState([]);
  const [loadingFuentes, setLoadingFuentes] = useState(true);
  const [fuenteSeleccionada, setFuenteSeleccionada] = useState("");
  const [camposDisponibles, setCamposDisponibles] = useState([]);
  const [camposSeleccionados, setCamposSeleccionados] = useState([]);
  const [reporteGenerado, setReporteGenerado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filtrosActivos, setFiltrosActivos] = useState({});
  const [filtrosAplicados, setFiltrosAplicados] = useState({});
  const [nombreReporte, setNombreReporte] = useState("Mi Reporte Personalizado");
  const [draggedItem, setDraggedItem] = useState(null);
  const [searchCampo, setSearchCampo] = useState("");
  
  const { getAuthHeaders, user } = useAuth();

  // Load dynamic data sources
  useEffect(() => {
    const fetchFuentes = async () => {
      try {
        setLoadingFuentes(true);
        const res = await axios.get(`${API}/reportes/fuentes-datos`, getAuthHeaders());
        setFuentesDatos(res.data);
      } catch (err) {
        console.error("Error loading data sources:", err);
        toast.error("Error al cargar fuentes de datos");
      } finally {
        setLoadingFuentes(false);
      }
    };
    fetchFuentes();
  }, [getAuthHeaders]);

  // Update fields when source changes
  useEffect(() => {
    if (fuenteSeleccionada) {
      const fuente = fuentesDatos.find(f => f.key === fuenteSeleccionada);
      if (fuente) {
        setCamposDisponibles(fuente.campos || []);
        setCamposSeleccionados([]);
        setReporteGenerado(null);
        setFiltrosActivos({});
        setFiltrosAplicados({});
      }
    }
  }, [fuenteSeleccionada, fuentesDatos]);

  // Get current source info
  const fuenteActual = fuentesDatos.find(f => f.key === fuenteSeleccionada);

  // Filtrar campos disponibles
  const camposFiltrados = camposDisponibles.filter(campo =>
    campo.label.toLowerCase().includes(searchCampo.toLowerCase()) ||
    campo.key.toLowerCase().includes(searchCampo.toLowerCase())
  );

  // Drag and Drop handlers
  const handleDragStart = (e, campo, source) => {
    setDraggedItem({ campo, source });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropToSelected = (e, targetIndex = null) => {
    e.preventDefault();
    if (!draggedItem) return;

    const { campo, source } = draggedItem;

    if (source === 'disponibles') {
      // Agregar campo a seleccionados
      if (!camposSeleccionados.find(c => c.key === campo.key)) {
        if (targetIndex !== null) {
          const newCampos = [...camposSeleccionados];
          newCampos.splice(targetIndex, 0, campo);
          setCamposSeleccionados(newCampos);
        } else {
          setCamposSeleccionados([...camposSeleccionados, campo]);
        }
      }
    } else if (source === 'seleccionados' && targetIndex !== null) {
      // Reordenar dentro de seleccionados
      const currentIndex = camposSeleccionados.findIndex(c => c.key === campo.key);
      if (currentIndex !== -1 && currentIndex !== targetIndex) {
        const newCampos = [...camposSeleccionados];
        newCampos.splice(currentIndex, 1);
        newCampos.splice(targetIndex, 0, campo);
        setCamposSeleccionados(newCampos);
      }
    }

    setDraggedItem(null);
  };

  const handleDropToAvailable = (e) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.source !== 'seleccionados') return;
    
    setCamposSeleccionados(camposSeleccionados.filter(c => c.key !== draggedItem.campo.key));
    setDraggedItem(null);
  };

  const agregarCampo = (campo) => {
    if (!camposSeleccionados.find(c => c.key === campo.key)) {
      setCamposSeleccionados([...camposSeleccionados, campo]);
    }
  };

  const removerCampo = (campo) => {
    setCamposSeleccionados(camposSeleccionados.filter(c => c.key !== campo.key));
  };

  const moverCampo = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= camposSeleccionados.length) return;
    
    const newCampos = [...camposSeleccionados];
    [newCampos[index], newCampos[newIndex]] = [newCampos[newIndex], newCampos[index]];
    setCamposSeleccionados(newCampos);
  };

  // Generar reporte
  const generarReporte = async () => {
    if (!fuenteSeleccionada || camposSeleccionados.length === 0) {
      toast.error("Selecciona una fuente de datos y al menos un campo");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/reportes/personalizado`,
        {
          fuente: fuenteSeleccionada,
          coleccion: fuenteActual?.coleccion || fuenteSeleccionada,
          campos: camposSeleccionados.map(c => c.key),
          filtros: {}
        },
        getAuthHeaders()
      );
      
      setReporteGenerado({
        ...res.data,
        columns: camposSeleccionados
      });
      toast.success("Reporte generado exitosamente");
    } catch (err) {
      console.error("Error generando reporte:", err);
      toast.error("Error al generar el reporte");
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros al reporte generado
  const aplicarFiltros = () => {
    if (!reporteGenerado) return;
    
    setFiltrosAplicados({...filtrosActivos});
    setShowFilters(false);
  };

  // Filtrar datos del reporte
  const datosFiltrados = reporteGenerado?.data?.filter(row => {
    return Object.entries(filtrosAplicados).every(([key, value]) => {
      if (!value || value === '' || value === 'all') return true;
      const rowValue = String(row[key] || '').toLowerCase();
      return rowValue.includes(String(value).toLowerCase());
    });
  }) || [];

  // Exportar reporte
  const exportarReporte = async (formato) => {
    if (!reporteGenerado) return;
    
    try {
      const res = await axios.post(
        `${API}/reportes/exportar`,
        {
          data: datosFiltrados,
          columns: camposSeleccionados,
          titulo: nombreReporte,
          formato: formato
        },
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const extensiones = { excel: 'xlsx', pdf: 'pdf', word: 'docx' };
      a.download = `${nombreReporte.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${extensiones[formato]}`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Reporte exportado a ${formato.toUpperCase()}`);
    } catch (err) {
      console.error("Error exportando:", err);
      toast.error("Error al exportar el reporte");
    }
  };

  const getFieldIcon = (type) => {
    switch(type) {
      case 'text': return Type;
      case 'number': return Hash;
      case 'date': 
      case 'datetime': return Calendar;
      case 'boolean': return ToggleLeft;
      case 'status': return CheckCircle2;
      default: return Type;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="reportes-personalizados-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => window.location.href = '/reportes'}
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#0C314C] dark:text-white flex items-center gap-2">
                <FileSpreadsheet className="w-7 h-7" />
                Constructor de Reportes
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Arrastra y suelta campos para crear tu reporte personalizado
              </p>
            </div>
          </div>
          
          {reporteGenerado && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportarReporte('excel')}
                className="border-green-300 text-green-600 hover:bg-green-50"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportarReporte('pdf')}
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                <FileType className="w-4 h-4 mr-1" />
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportarReporte('word')}
                className="border-blue-300 text-blue-600 hover:bg-blue-50"
              >
                <FileText className="w-4 h-4 mr-1" />
                Word
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Panel Izquierdo - Fuente y Campos Disponibles */}
          <div className="col-span-3 space-y-4">
            {/* Selección de Fuente */}
            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Database className="w-4 h-4 text-[#0C314C]" />
                  Fuente de Datos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingFuentes ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <Select value={fuenteSeleccionada} onValueChange={setFuenteSeleccionada}>
                    <SelectTrigger data-testid="fuente-datos-select">
                      <SelectValue placeholder="Seleccionar fuente..." />
                    </SelectTrigger>
                    <SelectContent>
                      {fuentesDatos.map((fuente) => {
                        const Icon = ICON_MAP[fuente.icono] || Database;
                        return (
                          <SelectItem key={fuente.key} value={fuente.key}>
                            <div className="flex items-center justify-between gap-2 w-full">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" />
                                {fuente.nombre}
                              </div>
                              <Badge variant="secondary" className="text-xs ml-2">
                                {fuente.registros}
                              </Badge>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
                {fuenteActual && (
                  <p className="text-xs text-slate-500 mt-2">
                    {fuenteActual.registros} registros disponibles • {fuenteActual.campos?.length || 0} campos
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Campos Disponibles */}
            {fuenteSeleccionada && (
              <Card 
                className="border-slate-200 dark:border-slate-700"
                onDragOver={handleDragOver}
                onDrop={handleDropToAvailable}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Columns className="w-4 h-4 text-[#0C314C]" />
                    Campos Disponibles
                  </CardTitle>
                  <div className="relative mt-2">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Buscar campo..."
                      value={searchCampo}
                      onChange={(e) => setSearchCampo(e.target.value)}
                      className="pl-8 h-9"
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[400px]">
                    <div className="p-3 space-y-1">
                      {camposFiltrados.map((campo) => {
                        const isSelected = camposSeleccionados.find(c => c.key === campo.key);
                        const Icon = getFieldIcon(campo.type);
                        
                        return (
                          <div
                            key={campo.key}
                            draggable={!isSelected}
                            onDragStart={(e) => handleDragStart(e, campo, 'disponibles')}
                            onClick={() => !isSelected && agregarCampo(campo)}
                            className={`
                              flex items-center gap-2 p-2 rounded-lg text-sm cursor-pointer transition-all
                              ${isSelected 
                                ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed' 
                                : 'bg-white dark:bg-slate-800 hover:bg-[#0C314C]/10 hover:border-[#0C314C] border border-slate-200 dark:border-slate-700'
                              }
                            `}
                          >
                            <GripVertical className="w-3 h-3 text-slate-400" />
                            <Icon className="w-4 h-4 text-slate-500" />
                            <span className="flex-1 truncate">{campo.label}</span>
                            {isSelected ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            ) : (
                              <Plus className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Panel Central - Campos Seleccionados (Columnas del Reporte) */}
          <div className="col-span-4 space-y-4">
            <Card className="border-slate-200 dark:border-slate-700 h-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Rows className="w-4 h-4 text-[#0C314C]" />
                    Columnas del Reporte
                    <Badge variant="outline" className="ml-2">{camposSeleccionados.length}</Badge>
                  </CardTitle>
                </div>
                <CardDescription className="text-xs">
                  Arrastra para reordenar las columnas
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div 
                  className="min-h-[400px] p-3"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDropToSelected(e)}
                >
                  {camposSeleccionados.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-8">
                      <Columns className="w-12 h-12 mb-3 text-slate-300" />
                      <p className="text-sm text-center">
                        Arrastra campos aquí o haz clic en ellos para agregarlos
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {camposSeleccionados.map((campo, index) => {
                        const Icon = getFieldIcon(campo.type);
                        return (
                          <div
                            key={campo.key}
                            draggable
                            onDragStart={(e) => handleDragStart(e, campo, 'seleccionados')}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDropToSelected(e, index)}
                            className="flex items-center gap-2 p-3 bg-[#0C314C]/5 dark:bg-[#0C314C]/20 border border-[#0C314C]/20 rounded-lg cursor-move group hover:border-[#0C314C]/40 transition-colors"
                          >
                            <GripVertical className="w-4 h-4 text-slate-400" />
                            <Badge variant="outline" className="bg-[#0C314C] text-white border-0 text-xs">
                              {index + 1}
                            </Badge>
                            <Icon className="w-4 h-4 text-[#0C314C]" />
                            <span className="flex-1 text-sm font-medium text-slate-700 dark:text-slate-300">
                              {campo.label}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removerCampo(campo)}
                              className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Panel Derecho - Configuración y Acciones */}
          <div className="col-span-5 space-y-4">
            {/* Nombre del Reporte */}
            <Card className="border-slate-200 dark:border-slate-700">
              <CardContent className="p-4">
                <Label className="text-sm text-slate-600 dark:text-slate-400">Nombre del Reporte</Label>
                <Input
                  value={nombreReporte}
                  onChange={(e) => setNombreReporte(e.target.value)}
                  placeholder="Mi Reporte Personalizado"
                  className="mt-1"
                />
              </CardContent>
            </Card>

            {/* Botón Generar */}
            <Button
              onClick={generarReporte}
              disabled={!fuenteSeleccionada || camposSeleccionados.length === 0 || loading}
              className="w-full bg-[#0C314C] hover:bg-[#2d5a8a] text-white h-12 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Generar Reporte
                </>
              )}
            </Button>

            {/* Resultados del Reporte */}
            {reporteGenerado && (
              <Card className="border-slate-200 dark:border-slate-700">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Eye className="w-4 h-4 text-[#0C314C]" />
                      Vista Previa
                      <Badge variant="outline">{datosFiltrados.length} registros</Badge>
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowFilters(true)}
                        className="h-7"
                      >
                        <Filter className="w-3 h-3 mr-1" />
                        Filtros
                        {Object.keys(filtrosAplicados).filter(k => filtrosAplicados[k]).length > 0 && (
                          <Badge className="ml-1 bg-[#0C314C] text-white text-xs h-4 px-1">
                            {Object.keys(filtrosAplicados).filter(k => filtrosAplicados[k]).length}
                          </Badge>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={generarReporte}
                        className="h-7"
                      >
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  {/* Export Buttons */}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                    <span className="text-xs text-slate-500 mr-2">Exportar:</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportarReporte('excel')}
                      className="h-7 border-green-300 text-green-600 hover:bg-green-50"
                      data-testid="export-excel-btn"
                    >
                      <FileSpreadsheet className="w-3 h-3 mr-1" />
                      Excel
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportarReporte('pdf')}
                      className="h-7 border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="export-pdf-btn"
                    >
                      <FileType className="w-3 h-3 mr-1" />
                      PDF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => exportarReporte('word')}
                      className="h-7 border-blue-300 text-blue-600 hover:bg-blue-50"
                      data-testid="export-word-btn"
                    >
                      <FileText className="w-3 h-3 mr-1" />
                      Word
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[350px]">
                    <Table>
                      <TableHeader className="sticky top-0 bg-white dark:bg-slate-900 z-10">
                        <TableRow>
                          {camposSeleccionados.map((col, idx) => (
                            <TableHead key={idx} className="text-xs font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                              {col.label}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {datosFiltrados.length === 0 ? (
                          <TableRow>
                            <TableCell 
                              colSpan={camposSeleccionados.length} 
                              className="text-center py-8 text-slate-500"
                            >
                              No se encontraron registros
                            </TableCell>
                          </TableRow>
                        ) : (
                          datosFiltrados.slice(0, 100).map((row, rowIdx) => (
                            <TableRow key={rowIdx} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                              {camposSeleccionados.map((col, colIdx) => (
                                <TableCell key={colIdx} className="text-xs text-slate-600 dark:text-slate-400">
                                  {col.type === 'status' ? (
                                    <Badge 
                                      variant="outline"
                                      className={
                                        row[col.key] === 'completado' || row[col.key] === 'completed' ? 'bg-green-100 text-green-700 border-green-300' :
                                        row[col.key] === 'en_proceso' || row[col.key] === 'processing' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                                        row[col.key] === 'pendiente' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                                        row[col.key] === 'cancelado' || row[col.key] === 'error' ? 'bg-red-100 text-red-700 border-red-300' :
                                        row[col.key] === 'abierto' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                                        row[col.key] === 'resuelto' ? 'bg-green-100 text-green-700 border-green-300' :
                                        ''
                                      }
                                    >
                                      {row[col.key]}
                                    </Badge>
                                  ) : col.type === 'boolean' ? (
                                    row[col.key] ? (
                                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <X className="w-4 h-4 text-slate-300" />
                                    )
                                  ) : col.type === 'date' && row[col.key] ? (
                                    new Date(row[col.key]).toLocaleDateString('es-ES')
                                  ) : col.type === 'datetime' && row[col.key] ? (
                                    new Date(row[col.key]).toLocaleString('es-ES')
                                  ) : (
                                    <span className="truncate max-w-[150px] block">{row[col.key] || '-'}</span>
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    {datosFiltrados.length > 100 && (
                      <div className="p-3 text-center text-xs text-slate-500 bg-slate-50 dark:bg-slate-800">
                        Mostrando primeros 100 de {datosFiltrados.length} registros. Exporta para ver todos.
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Dialog de Filtros */}
        <Dialog open={showFilters} onOpenChange={setShowFilters}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-[#0C314C]" />
                Filtros del Reporte
              </DialogTitle>
              <DialogDescription>
                Aplica filtros a los datos del reporte generado
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {camposSeleccionados.map((campo) => (
                <div key={campo.key}>
                  <Label className="text-sm text-slate-600">{campo.label}</Label>
                  {campo.type === 'status' ? (
                    <Select 
                      value={filtrosActivos[campo.key] || 'all'} 
                      onValueChange={(v) => setFiltrosActivos({...filtrosActivos, [campo.key]: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="en_proceso">En Proceso</SelectItem>
                        <SelectItem value="completado">Completado</SelectItem>
                        <SelectItem value="cancelado">Cancelado</SelectItem>
                        <SelectItem value="abierto">Abierto</SelectItem>
                        <SelectItem value="resuelto">Resuelto</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : campo.type === 'boolean' ? (
                    <Select 
                      value={filtrosActivos[campo.key] || 'all'} 
                      onValueChange={(v) => setFiltrosActivos({...filtrosActivos, [campo.key]: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="true">Sí</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={filtrosActivos[campo.key] || ''}
                      onChange={(e) => setFiltrosActivos({...filtrosActivos, [campo.key]: e.target.value})}
                      placeholder={`Filtrar por ${campo.label.toLowerCase()}...`}
                      className="mt-1"
                    />
                  )}
                </div>
              ))}
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setFiltrosActivos({});
                  setFiltrosAplicados({});
                }}
              >
                Limpiar
              </Button>
              <Button 
                onClick={aplicarFiltros}
                className="bg-[#0C314C] hover:bg-[#2d5a8a] text-white"
              >
                Aplicar Filtros
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
